import { NoSsr } from '@mui/material';
import {
  CategoryTypes,
  type HeadlineResponse,
  type LatestForumPostsResponse,
  type NilHundredResponseNil100FilterResponseRelatedPagedData,
  type PlayerItemResponsePagedData,
  type ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  type TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { MobileMPU } from 'components/Ads/MobileMPU';
import { MPU } from 'components/Ads/MPU';
import { PostFeed } from 'components/Forums/Forums';
import { CoverList } from 'components/Headlines/ArticleCover/CoverList';
import { FeedList } from 'components/Headlines/ArticleFeed/FeedList';
import { TopHeadlinesList } from 'components/Headlines/TopHeadlines/TopHeadlinesList';
import { HomeLayout } from 'components/Layouts/HomeLayout';
import { SignUpFooter } from 'components/Newsletter/SignUpFooter';
import { Nil100Widget } from 'components/Nil/Nil100Widget';
import { PianoPromo } from 'components/Promos/PianoPromo';
import { ScheduleModule } from 'components/Schedules/Sidebar/Module';
import { TeamConnect } from 'components/Social/TeamConnect';
import { LatestCommits } from 'components/Teams/LatestCommits';
import { TopHeadlines } from 'components/TopHeadlines';
import { useMemo } from 'react';
import { getArticleLists } from 'utils/articles';
import { createTitle } from 'utils/meta';
import { hasSiteSportCategory } from 'utils/sites';

import styles from './RivalsHome.module.scss';

export interface IRivalsHomeProps {
  featuredArticles: HeadlineResponse[] | null;
  siteRecruitingArticles: HeadlineResponse[] | null;
  siteHighSchoolArticles: HeadlineResponse[] | null;
  siteForums: LatestForumPostsResponse | null;
  channelSportsArticles: HeadlineResponse[] | null;
  channelRecruitingArticles: HeadlineResponse[] | null;
  commitsList: PlayerItemResponsePagedData | null;
  nil100List: NilHundredResponseNil100FilterResponseRelatedPagedData | null;
  schedule: ScoreboardResponseRelatedScheduleResponseRelatedPagedData | null;
  trendingList:
    | TrendingListItemResponseTrendingListResponseRelatedPagedData[]
    | TrendingListItemResponseTrendingListResponseRelatedPagedData
    | null;
}

export const RivalsHome = ({
  featuredArticles,
  siteRecruitingArticles,
  siteHighSchoolArticles,
  channelSportsArticles,
  channelRecruitingArticles,
  siteForums,
  commitsList,
  nil100List,
  schedule,
  trendingList,
}: IRivalsHomeProps) => {
  const { currentSite, siblingSite } = useSite();

  const { list: nil100list } = nil100List || {};
  const { siteName, key: siteKey, url: siteUrl } = currentSite || {};
  const siteColor = currentSite?.primaryColor || '#fe3b1f';
  const siteCategories = useMemo(
    () => currentSite?.siteCategories || [],
    [currentSite],
  );

  const topHeadlinesHeading = createTitle(
    'Top',
    currentSite?.organization?.knownAs || currentSite?.organization?.name,
    'Headlines',
  );

  const [
    featuredArticlesList,
    siteRecruitingArticlesList,
    channelSportsArticlesList,
    channelRecruitingArticlesList,
    highSchoolNews,
  ] = getArticleLists({
    featuredArticles: {
      list: featuredArticles ?? [],
      sliceLimit: 8,
    },
    lists: [
      {
        list: siteRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: channelSportsArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: channelRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: siteHighSchoolArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
    ],
  });

  const sportViewAll = useMemo(
    () =>
      [
        { sport: 'Football', slug: 'football' },
        { sport: 'Basketball', slug: 'basketball' },
      ]
        .filter(({ sport }) =>
          hasSiteSportCategory(siteCategories, CategoryTypes.Sport, sport),
        )
        .map(({ sport, slug }) => ({
          url: `${siteUrl}/category/${slug}/news/`,
          text: `View All ${sport}`,
          title: `${sport} News`,
        })),
    [siteUrl, siteCategories],
  );

  const recruitingViewAll = useMemo(
    () =>
      [
        { sport: 'Football', slug: 'football-recruiting' },
        { sport: 'Basketball', slug: 'basketball-recruiting' },
      ]
        .filter(({ sport }) =>
          hasSiteSportCategory(siteCategories, CategoryTypes.Recruiting, sport),
        )
        .map(({ sport, slug }) => ({
          url: `${siteUrl}/category/${slug}/news/`,
          text: `View All ${sport} Recruiting`,
          title: `${sport} Recruiting News`,
        })),
    [siteUrl, siteCategories],
  );

  const highSchoolViewAll = useMemo(
    () =>
      [
        { sport: 'Football', slug: 'high-school-football' },
        { sport: 'Basketball', slug: 'high-school-basketball' },
      ]
        .filter(({ sport }) =>
          hasSiteSportCategory(siteCategories, CategoryTypes.HighSchool, sport),
        )
        .map(({ sport, slug }) => ({
          url: `${siteUrl}/category/${slug}/news/`,
          text: `View All ${sport} High School`,
          title: `${sport} High School News`,
        })),
    [siteUrl, siteCategories],
  );

  const forumViewAll = siteForums?.primaryForum?.url
    ? [
        {
          url: siteForums.primaryForum.url,
          text: 'View All',
          title: 'View All Message Board Posts',
        },
      ]
    : [];

  const topHeadlinesLinks = [
    {
      title: 'View All',
      href: `/college/${siblingSite?.slug}/news/`,
    },
    {
      title: createTitle(
        siblingSite?.organization?.knownAs || siblingSite?.organization?.name,
        'Channel',
      ),
      href: `${siblingSite?.url}/`,
    },
  ];

  return (
    <div className={styles.layout}>
      <HomeLayout>
        <section className={styles.center}>
          <NoSsr>
            <PianoPromo className={styles.pianoPromo} id="pianoPromoRivals" />
          </NoSsr>
          <div className={styles.inner}>
            {!!featuredArticlesList?.length && (
              <div className={styles.coverArticles}>
                <CoverList
                  backgroundColor="transparent"
                  categoryColor={siteColor}
                  isHome
                  list={featuredArticlesList.slice(0, 3)}
                />
              </div>
            )}

            <div className={styles.articleLists}>
              {!!featuredArticlesList?.length && (
                <FeedList
                  list={featuredArticlesList.slice(3, 8)}
                  section={`${siteName} Headlines`}
                  viewAllLinks={sportViewAll}
                />
              )}

              <MobileMPU id={'mobile_mpu_top'} incontent />
              {!!siteRecruitingArticlesList?.length && (
                <FeedList
                  list={siteRecruitingArticlesList}
                  section={`${siteName} Recruiting`}
                  viewAllLinks={recruitingViewAll}
                />
              )}

              {!!highSchoolNews?.length && (
                <FeedList
                  list={highSchoolNews}
                  section={`${siteName} High School`}
                  viewAllLinks={highSchoolViewAll}
                />
              )}
            </div>
          </div>
        </section>
        <aside className={styles.right}>
          <div className={styles.inner}>
            <MPU id={'mpu_top'} />

            {!!siteForums?.posts?.length && (
              <PostFeed
                forum={siteForums?.primaryForum}
                links={forumViewAll}
                posts={siteForums?.posts}
                title="Popular Message Board Posts"
              />
            )}

            {!!nil100list?.length && (
              <Nil100Widget isModule list={nil100list?.slice(0, 5)} />
            )}

            <MPU id={'mpu_middle'} />
            <MobileMPU id={'mobile_mpu_middle'} incontent />
          </div>
        </aside>
        <aside className={styles.left}>
          <div className={styles.inner}>
            {(!!channelRecruitingArticlesList?.length ||
              !!channelSportsArticlesList?.length) && (
              <div className={styles.topHeadlines}>
                <TopHeadlinesList
                  heading={topHeadlinesHeading}
                  isModule
                  links={topHeadlinesLinks}
                  list={[
                    ...(channelSportsArticlesList || []),
                    ...(channelRecruitingArticlesList || []),
                  ].slice(0, 5)}
                  variant="secondary"
                />
              </div>
            )}

            {!!commitsList?.list?.length && (
              <LatestCommits list={commitsList?.list} />
            )}

            {!!schedule?.list?.length && (
              <ScheduleModule
                list={schedule?.list}
                relatedModel={schedule?.relatedModel}
              />
            )}

            {!!trendingList && <TopHeadlines trendingList={trendingList} />}

            <TeamConnect />
          </div>
        </aside>
      </HomeLayout>
      <SignUpFooter titleText={`${siteName} Newsletter`} />
    </div>
  );
};
